import { useCallback, useEffect, useRef } from 'react'
import semver from 'semver'
import ElectronIPC from './ElectronIPC'
import { env } from './EnvironmentVariables'
import { logger } from './Logger'
import { keyToRobotjsMapping, mouseButtonToRobotJsMapping } from './robotjsValidKeycodes'
import useAppContextRef from './useAppContextRef'
import useRoomContextRef from './useRoomContextRef'
import useUserContextRef from './useUserContextRef'
import useRemoteDesktopControlPermissionActions from './useRemoteDesktopControlPermissionActions'
import { ExternalEventEmitter } from '../ExternalEventEmitter'
import { ExternalEvent } from '../AppContext'

/**
 * NOTES
 *
 * Mac modifier key handling:
 * For example, for CTRL+C:
 *
 *     robotjs.keyTap('c', ['command'])
 *     robotjs.keyToggle('command', 'up')
 *
 * Otherwise, the command key gets "stuck" until it is escaped by another button press
 * such as ESC.
 *
 * We do not do a `robotjs.keyToggle('command', 'down')` at the beginning on command key
 * down because for example, on Logic's shortcut key input detector field,
 * `keyToggle('command', 'down')` causes Logic to immediately detect the command key as
 * the shortcut without listening for more keys.
 *
 *  * ^^^ The above issue has been resolved by patching robotjs -- see commit
 * https://github.com/sungeunchoi-kr/robotjs/commit/65edad8e95c018e3eee6b7dcae0a414d6c41d3c6
 * .
 */

const log = logger.createChildLogger({ name: 'RemoteDesktopControlClient' })

export default function useRemoteDesktopControlClient() {
    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()
    const { appStateRef } = useAppContextRef()

    const doubleClickDetectionTimerRef = useRef<NodeJS.Timer | null>(null)

    const isMouseButtonDown = useRef<boolean>(false)
    const modifierKeysState = useRef<{
        shift: boolean
        control: boolean
        alt: boolean
        command: boolean
    }>({
        shift: false,
        control: false,
        alt: false,
        command: false,
    })

    const RemoteDesktopControlPermissions = useRemoteDesktopControlPermissionActions()

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName === 'hotkey') {
                if (externalEvent.value.key === 'stop_screen_control') {
                    RemoteDesktopControlPermissions.revoke()
                }
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    const rdpHandler = useCallback((callerSessionId: string, opcode: string, data: any) => {
        log.isSilly() && log.silly('rdpHandler', `received opcode=${opcode} data=${JSON.stringify(data)}`)

        const permittedPeerSessionId =
            appStateRef.current.appState.remoteDesktopBeingControlledStatus?.sessionId || null
        if (permittedPeerSessionId && permittedPeerSessionId !== callerSessionId) {
            // Not authorized.
            log.isSilly() &&
                log.silly(
                    'rdpHandler',
                    `not authorized: permittedPeerSessionId=${permittedPeerSessionId} !== callerSessionId=${callerSessionId}.`
                )
            return
        }

        const callerAppVersion = roomStateRef.current.peers[callerSessionId]?.appVersion || null

        // Detect double-click. On Mac, we have to specifically call `mouseClick("left", double=true)`.
        // See https://github.com/octalmage/robotjs/pull/70.
        let isDoubleClick = false
        if (env.OS === 'darwin' && opcode === 'mousedown') {
            if (doubleClickDetectionTimerRef.current) {
                // then this mousedown is a double-click mousedown.
                isDoubleClick = true
            } else {
                // set timer
                doubleClickDetectionTimerRef.current = setTimeout(() => {
                    doubleClickDetectionTimerRef.current = null
                }, 250)
            }
        }
        if (isDoubleClick) {
            // override
            opcode = 'double-click'
        }

        let screenWidth = null
        let screenHeight = null
        let scaleFactor = 1
        let screenPositionX = 0
        let screenPositionY = 0
        // nonScaledBounds - windows only; use this whenever you can as it gives the non-scaled
        // bounds and we don't have to mess with scaleFactor which does not cover all the cases.
        let nonScaledBounds = null
        const { desktopSourceInfo } = userStateRef.current.mediaSelection
        if (desktopSourceInfo) {
            screenWidth = desktopSourceInfo.screenWidth
            screenHeight = desktopSourceInfo.screenHeight
            screenPositionX = desktopSourceInfo.screenPositionX || 0
            screenPositionY = desktopSourceInfo.screenPositionY || 0
            let hasValidNonScaledBounds =
                desktopSourceInfo.nonScaledBounds &&
                !isNaN(desktopSourceInfo.nonScaledBounds.x) &&
                !isNaN(desktopSourceInfo.nonScaledBounds.y) &&
                !isNaN(desktopSourceInfo.nonScaledBounds.width) &&
                !isNaN(desktopSourceInfo.nonScaledBounds.height)
            nonScaledBounds = hasValidNonScaledBounds ? desktopSourceInfo.nonScaledBounds : null

            let ignoreScaleFactor = appStateRef.current.appSettings.screenControlIgnoreScaleFactor
            if (ignoreScaleFactor === null) {
                // handle setting 'default' case.
                if (env.OS === 'darwin') {
                    ignoreScaleFactor = true
                } else {
                    ignoreScaleFactor = false
                }
            }

            if (!ignoreScaleFactor && desktopSourceInfo.scaleFactor) {
                // the scaleFactor adjustment only applies to Windows.
                scaleFactor = desktopSourceInfo.scaleFactor
            }

            log.isSilly() &&
                log.silly(
                    'rdpHandler',
                    `screenWidth=${screenWidth}, screenHeight=${screenHeight}, ` +
                        `screenPosition=[${screenPositionX}, ${screenPositionY}) ` +
                        `ignoreScaleFactor=${ignoreScaleFactor}, scaleFactor=${scaleFactor}, ` +
                        `nonScaledBounds=${JSON.stringify(nonScaledBounds)}`
                )
        } else {
            log.silly('rdpHandler', `desktopSourceInfo is null.`)
        }

        switch (opcode) {
            case 'mouseup':
                {
                    // xp, yp is the position of the cursor in percentage relative to the
                    // display size, in range [0, 1]. These were introduced in v1.4.4.
                    // mod: modifer keys { a: alt, c: control, m: meta, s: shift }; field was introduced in v2.1.4.
                    const { x, y, xp, yp, b, mod } = data
                    isMouseButtonDown.current = false

                    let coord: [number, number]
                    if (xp && yp && screenWidth && screenHeight) {
                        if (nonScaledBounds) {
                            coord = [
                                xp * nonScaledBounds.width + nonScaledBounds.x,
                                yp * nonScaledBounds.height + nonScaledBounds.y,
                            ]
                        } else {
                            coord = [
                                xp * screenWidth * scaleFactor + screenPositionX,
                                yp * screenHeight * scaleFactor + screenPositionY,
                            ]
                        }
                    } else {
                        coord = [x * scaleFactor + screenPositionX, y * scaleFactor + screenPositionY] // fallback
                    }

                    ElectronIPC.robotJSCommand('moveMouse', coord)
                    ElectronIPC.robotJSCommand('mouseToggle', ['up', mouseButtonToRobotJsMapping[b]])
                }
                break
            case 'mousedown':
                {
                    const { x, y, xp, yp, b, mod } = data
                    isMouseButtonDown.current = true

                    let coord: [number, number]
                    if (xp && yp && screenWidth && screenHeight) {
                        if (nonScaledBounds) {
                            coord = [
                                xp * nonScaledBounds.width + nonScaledBounds.x,
                                yp * nonScaledBounds.height + nonScaledBounds.y,
                            ]
                        } else {
                            coord = [
                                xp * screenWidth * scaleFactor + screenPositionX,
                                yp * screenHeight * scaleFactor + screenPositionY,
                            ]
                        }
                    } else {
                        coord = [x * scaleFactor + screenPositionX, y * scaleFactor + screenPositionY] // fallback
                    }

                    // if there is a mismatch btwn `mod` and `modifierKeysState`, then override.
                    // TODO: work in progress
                    /* if (typeof mod === 'object') {
                    if (mod.a !== modifierKeysState.current.alt) {
                        modifierKeysState.current.alt = mod.a
                    }
                } */

                    ElectronIPC.robotJSCommand('moveMouse', coord)
                    ElectronIPC.robotJSCommand('mouseToggle', ['down', mouseButtonToRobotJsMapping[b]])
                }
                break
            case 'mousemove':
                {
                    const { x, y, xp, yp } = data

                    let coord: [number, number]
                    if (xp && yp && screenWidth && screenHeight) {
                        if (nonScaledBounds) {
                            coord = [
                                xp * nonScaledBounds.width + nonScaledBounds.x,
                                yp * nonScaledBounds.height + nonScaledBounds.y,
                            ]
                        } else {
                            coord = [
                                xp * screenWidth * scaleFactor + screenPositionX,
                                yp * screenHeight * scaleFactor + screenPositionY,
                            ]
                        }
                    } else {
                        coord = [x * scaleFactor + screenPositionX, y * scaleFactor + screenPositionY] // fallback
                    }

                    log.isSilly() &&
                        log.silly('rdpHandler:mousemove', `call robotJSCommand: coord=${JSON.stringify(coord)}`)
                    if (isMouseButtonDown.current) {
                        ElectronIPC.robotJSCommand('dragMouse', coord)
                    } else {
                        ElectronIPC.robotJSCommand('moveMouse', coord)
                    }
                }
                break
            case 'click':
                {
                    const { button, double } = data
                    const typeMapping = ['left', 'middle', 'right']
                    const type = double ? 'left' : typeMapping[button]
                    // console.log(`click: button=${button} type=${type}`)
                    if (!type) {
                        return
                    }

                    if (isMouseButtonDown.current) {
                        isMouseButtonDown.current = false
                        // ElectronIPC.robotJSCommand('mouseToggle', [ 'up' ])
                    }

                    // console.log(`clicking. (isDoubleClick=${double})`)
                    ElectronIPC.robotJSCommand('mouseClick', [type, double])
                }
                break
            case 'double-click':
                ElectronIPC.robotJSCommand('mouseClick', [
                    mouseButtonToRobotJsMapping[data.b],
                    true, // double-click
                ])
                break
            case 'keyup':
            case 'keydown':
                {
                    // mod: modifer keys { a: alt, c: control, m: meta, s: shift }
                    // `mod` field introduced in v2.1.4.
                    let { code, mod } = data

                    if (!code || typeof code.toLowerCase !== 'function') {
                        return
                    }

                    code = code.toLowerCase()
                    if (code.startsWith('key')) {
                        code = code.replace('key', '')
                    } else if (code.startsWith('digit')) {
                        code = code.replace('digit', '')
                    } else if (code.startsWith('numpad')) {
                        if (code === 'numpadadd') {
                            code = '+'
                        } else if (code === 'numpadsubtract') {
                            code = '-'
                        } else if (code === 'numpadmultiply') {
                            code = '*'
                        } else if (code === 'numpaddivide') {
                            code = '/'
                        } else if (code === 'numpaddecimal') {
                            code = '.'
                        } else if (code === 'numpadenter') {
                            code = 'enter'
                        } else {
                            code = code.replace('numpad', 'numpad_')
                        }
                    } else if (code.startsWith('arrow')) {
                        code = code.replace('arrow', '')
                    } else if (code === 'shiftright' || code === 'shiftleft') {
                        code = 'shift'
                        modifierKeysState.current.shift = opcode === 'keydown'
                    } else if (code === 'controlleft' || code === 'controlright') {
                        code = 'control'
                        modifierKeysState.current.control = opcode === 'keydown'
                    } else if (code === 'altleft' || code === 'altright') {
                        code = 'alt'
                        modifierKeysState.current.alt = opcode === 'keydown'
                    } else if (code === 'metaleft' || code === 'metaright') {
                        code = 'command'
                        modifierKeysState.current.command = opcode === 'keydown'
                    } else {
                        code = (keyToRobotjsMapping as any)[code]
                    }

                    if (code == null) {
                        return
                    }

                    // we handle `modiferKeysState` this way for backward compatibility reasons.
                    if (typeof mod === 'object') {
                        modifierKeysState.current.alt = mod.a
                        modifierKeysState.current.control = mod.c
                        modifierKeysState.current.shift = mod.s

                        // 'mod.m' works only for v2.1.9 and above.
                        try {
                            if (semver.gte(callerAppVersion || '0.0.0', '2.1.9')) {
                                modifierKeysState.current.command = mod.m
                            }
                        } catch (e) {
                            console.error(e)
                        }
                    }

                    let modifiers = []
                    if (modifierKeysState.current.shift) modifiers.push('shift')
                    if (modifierKeysState.current.control) modifiers.push('control')
                    if (modifierKeysState.current.alt) modifiers.push('alt')
                    if (modifierKeysState.current.command) modifiers.push('command')

                    if (opcode === 'keydown') {
                        ElectronIPC.robotJSCommand('keyToggle', [code, 'down', modifiers])
                    } else if (opcode === 'keyup') {
                        ElectronIPC.robotJSCommand('keyToggle', [code, 'up', modifiers])
                    }

                    log.isSilly() &&
                        log.silly('rdpHandler:keydown', `call robotJSCommand.`, {
                            opcode,
                            code,
                            modifierKeysState: modifierKeysState.current,
                        })
                }
                break
            case 'wheel':
                {
                    let { deltaX, deltaY } = data

                    deltaX *= -1
                    deltaY *= -1

                    if (appStateRef.current.appSettings.screenControlWheelDirectionInvert) {
                        deltaX *= -1
                        deltaY *= -1
                    }

                    ElectronIPC.robotJSCommand('scrollMouse', [deltaX, deltaY])
                }
                break
        }
    }, [])

    return {
        rdpHandler,
    }
}
